/**
 * ActionsBuilder Component
 *
 * Component for managing application actions (permissions).
 * Allows users to add/remove actions that define what operations
 * are valid for the application's permission system.
 *
 * Actions are used in the permission format: <applicationCode>:<action>
 */

import React, { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

/**
 * Common predefined actions that users can quickly add
 */
const SUGGESTED_ACTIONS = [
  { label: 'read', description: 'View/read data' },
  { label: 'write', description: 'Create new data' },
  { label: 'update', description: 'Modify existing data' },
  { label: 'delete', description: 'Remove data' },
  { label: 'list', description: 'List/enumerate items' },
  { label: 'export', description: 'Export data' },
  { label: 'import', description: 'Import data' },
  { label: 'execute', description: 'Run/execute operations' },
];

interface ActionsBuilderProps {
  actions: string[];
  onChange: (actions: string[]) => void;
  disabled?: boolean;
  error?: string;
}

export const ActionsBuilder: React.FC<ActionsBuilderProps> = ({
  actions,
  onChange,
  disabled = false,
  error,
}) => {
  const [customAction, setCustomAction] = useState<string>('');

  // Add action (from suggested or custom)
  const addAction = (action: string) => {
    const normalizedAction = action.toLowerCase().trim();

    if (!normalizedAction) return;

    // Validate action format
    if (!/^[a-z][a-z0-9_-]*$/.test(normalizedAction)) {
      alert('Action must be lowercase and contain only letters, numbers, hyphens, and underscores');
      return;
    }

    if (actions.includes(normalizedAction)) {
      return; // Already exists, do nothing
    }

    onChange([...actions, normalizedAction]);
    setCustomAction('');
  };

  // Remove action
  const removeAction = (actionToRemove: string) => {
    onChange(actions.filter((a) => a !== actionToRemove));
  };

  // Handle custom action input
  const handleCustomActionSubmit = () => {
    if (customAction.trim()) {
      addAction(customAction);
    }
  };

  // Get suggested actions that aren't already added
  const availableSuggestions = SUGGESTED_ACTIONS.filter(
    (s) => !actions.includes(s.label)
  );

  return (
    <div className="space-y-4">
      {/* Current Actions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Current Actions ({actions.length})
        </label>
        {actions.length > 0 ? (
          <div className="border border-gray-300 rounded-md p-3 bg-gray-50">
            <div className="flex flex-wrap gap-2">
              {actions.map((action) => (
                <Badge
                  key={action}
                  variant="info"
                  className="flex items-center gap-1 pr-1"
                >
                  <span className="font-mono">{action}</span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => removeAction(action)}
                      className="ml-1 hover:text-red-600 rounded-full p-0.5 hover:bg-red-100"
                      aria-label={`Remove ${action}`}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 rounded-md p-4 text-center text-gray-500 text-sm">
            No actions defined yet. Add at least one action below.
          </div>
        )}
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>

      {/* Quick Add Suggestions */}
      {!disabled && availableSuggestions.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quick Add
          </label>
          <div className="flex flex-wrap gap-2">
            {availableSuggestions.map((suggestion) => (
              <button
                key={suggestion.label}
                type="button"
                onClick={() => addAction(suggestion.label)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 transition-colors"
                title={suggestion.description}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                {suggestion.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Action Input */}
      {!disabled && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Action
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="e.g., approve, archive, publish"
                value={customAction}
                onChange={(e) => setCustomAction(e.target.value.toLowerCase())}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCustomActionSubmit();
                  }
                }}
                disabled={disabled}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCustomActionSubmit}
              disabled={disabled || !customAction.trim()}
            >
              Add
            </Button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Actions must be lowercase, start with a letter, and contain only letters, numbers, hyphens, and underscores.
          </p>
        </div>
      )}
    </div>
  );
};
